import { Skeleton } from 'antd';
import { PAGE_SIZE } from 'modules/api/constants';
import { useAppDispatch, useAppSelector } from 'modules/hooks/appHooks';
import {
  LeasingCalculatorSelectors,
  LeasingCalculatorThunks,
} from 'modules/leasingCalculator/redux';
import { LeasingPremadeProduct } from 'modules/types/LeasingCalculator';
import { SortingParams, SortType } from 'modules/types/PagingParams';
import { Button, ButtonType, theme } from 'modules/ui';
import { BankProductAndServiceFinancing } from 'modules/ui/icons';
import { TableHookProps } from 'modules/ui/pageWithTable/TableWithSearch';
import { numberToLocaleString } from 'modules/utils';
import { StyledSubtitle } from 'modules/utils/tableCellCssUtils';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { productSelectionMapper } from 'modules/utils/mappers/leasingCalculatorCodesMapper';
import { LeasingCalculatorCodesSelectors } from 'modules/hooks/codesHooks/redux/leasingCalculatorCodes';
import { getLeasingCalculatorPageUrl } from '../../common/helper';

export const usePremadeProductsActions = (props?: TableHookProps) => {
  const { formData } = props || {};
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const premadeProducts = useAppSelector(
    LeasingCalculatorSelectors.selectPremadeProducts,
  );
  const totalPages = useAppSelector(
    LeasingCalculatorSelectors.selectPremadeProductsTotalPageCount,
  );
  const loading = useAppSelector(
    LeasingCalculatorSelectors.selectPremadeProductsLoading,
  );

  const leasingObjectAges = useAppSelector(
    LeasingCalculatorCodesSelectors.selectLeasingObjectAgeCodes,
  );

  const getLeasingOffer = useCallback(
    async (premadeProduct: LeasingPremadeProduct) => {
      const productSelectionData = productSelectionMapper(
        premadeProduct,
        formData || {},
        leasingObjectAges || [],
      );

      const result = await dispatch(
        LeasingCalculatorThunks.submitProductSelectionData(
          productSelectionData,
        ),
      );

      if (result.meta.requestStatus === 'fulfilled') {
        history.push(getLeasingCalculatorPageUrl(5));
      }
    },
    [dispatch, formData, history, leasingObjectAges],
  );

  const fetchMorePremadeProducts = useCallback(
    (page: number, sort?: SortingParams[], search?: string) => {
      dispatch(
        LeasingCalculatorThunks.fetchPremadeProducts({
          params: {
            page,
            size: PAGE_SIZE,
            search,
            sort,
          },
          calculatorData: formData || {},
        }),
      );
    },
    [dispatch, formData],
  );

  const getProductsColumns = useMemo(
    () => [
      {
        label: t('leasingCalculator.productSelectionData.productName'),
        accessor: 'productName',
        sort: SortType.DEFAULT,
      },
      {
        label: t('leasingCalculator.productSelectionData.interestRateType'),
        accessor: 'interestRateType',
        sort: SortType.DEFAULT,
      },
      {
        label: t('leasingCalculator.productSelectionData.referentInterestRate'),
        accessor: 'referentInterestRate',
        sort: SortType.DEFAULT,
      },
      {
        label: t('leasingCalculator.productSelectionData.margin'),
        accessor: 'margin',
        sort: SortType.DEFAULT,
      },
      {
        label: t('leasingCalculator.productSelectionData.interestRate'),
        accessor: 'interestRate',
        sort: SortType.DEFAULT,
      },
      {
        label: t('leasingCalculator.productSelectionData.processingCost'),
        accessor: 'cost',
        sort: SortType.DEFAULT,
      },
      {
        label: t('leasingCalculator.productSelectionData.action'),
        accessor: 'action',
        mobileOnly: true,
      },
    ],
    [t],
  );

  const getProductsObject = useMemo(
    () =>
      premadeProducts?.map((product: LeasingPremadeProduct) => ({
        ...product,
        interestRateType: product.interestRateType?.name,
        referentInterestRate: product.referentInterestRate?.name,
        margin: (
          <StyledSubtitle>
            {product.margin
              ? `${numberToLocaleString(product.margin, 0)}%`
              : '-'}
          </StyledSubtitle>
        ),
        interestRate: (
          <StyledSubtitle>
            {numberToLocaleString(product.interestRate, 0, 3)} %
          </StyledSubtitle>
        ),
        cost: (
          <StyledSubtitle>
            {numberToLocaleString(product.cost, 0)} %
          </StyledSubtitle>
        ),
        action: (
          <Button
            type={ButtonType.Action}
            text={t('leasingCalculator.productSelectionData.informativeOffer')}
            icon={<BankProductAndServiceFinancing />}
            color={theme.colors.primaryT2}
            onClick={() => getLeasingOffer(product)}
            hoverColor={theme.colors.primaryT1}
          />
        ),
      })) || [],
    [getLeasingOffer, premadeProducts, t],
  );

  const getSkeletonObject = useMemo(
    () =>
      [...Array(10)].map(() => ({
        productName: <Skeleton.Input active size="small" />,
        interestRateType: <Skeleton.Input active size="small" />,
        referentInterestRate: <Skeleton.Input active size="small" />,
        margin: <Skeleton.Input active size="small" />,
        interestRate: <Skeleton.Input active size="small" />,
        cost: <Skeleton.Input active size="small" />,
      })) || [],
    [],
  );

  const tableRows = getProductsObject;
  const [tableColumns, setTableColumns] = useState(getProductsColumns);
  const emptyMessageText = t(
    'leasingCalculator.productSelectionData.empty.noProducts',
  );

  return {
    skeletonObject: getSkeletonObject,
    fetchMoreData: fetchMorePremadeProducts,
    totalPages,
    loading,
    tableColumns,
    tableRows,
    emptyMessageText,
    setTableColumns,
  };
};
